// Reusable client-side pagination for admin tables (Downloads, Messages).
//
// Slices the full `items` array based on `page` + `pageSize` and exposes
// Prev / Next buttons, a numeric page jumper, and a page-size dropdown.
// Keeps everything controlled from the parent (no internal state here)
// so filters can reset `page=1` without hidden book-keeping.

import { CaretLeft, CaretRight } from "@phosphor-icons/react";

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Slice helper — parent passes `items` + current `page`/`pageSize` and
 * gets back the visible slice. Kept as a tiny named export so parents
 * don't duplicate the maths.
 */
export function paginate(items, page, pageSize) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export default function Pagination({
  totalItems,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  testidPrefix = "pagination",
}) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, totalItems);

  const go = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    if (next !== safePage) onPageChange(next);
  };

  // Nothing to paginate — hide the whole control (keeps empty states tidy).
  if (totalItems === 0) return null;

  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-3 border-t border-black/10 bg-neutral-50 text-xs flex-wrap"
      data-testid={testidPrefix}
    >
      <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500" data-testid={`${testidPrefix}-range`}>
        {start}–{end} of {totalItems}
      </div>

      <div className="flex items-center gap-3 ml-auto">
        <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-neutral-500">
          Rows
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-black/15 bg-white py-1 px-1.5 font-mono text-[11px]"
            data-testid={`${testidPrefix}-size`}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => go(safePage - 1)}
            disabled={safePage === 1}
            className="w-7 h-7 inline-flex items-center justify-center border border-black/15 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`${testidPrefix}-prev`}
            aria-label="Previous page"
          >
            <CaretLeft size={12} weight="bold" />
          </button>
          <span
            className="px-2 font-mono text-[11px] tabular-nums text-neutral-700"
            data-testid={`${testidPrefix}-current`}
          >
            {safePage} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => go(safePage + 1)}
            disabled={safePage === totalPages}
            className="w-7 h-7 inline-flex items-center justify-center border border-black/15 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`${testidPrefix}-next`}
            aria-label="Next page"
          >
            <CaretRight size={12} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
